<template>
  <div>
    <v-form>
      <v-row>
        <v-col md="6" cols="12" v-if="context !== 'create'">
          <v-text-field
            v-model="localReport.name"
            :label="$tc('name')"
            :error-messages="nameErrors"
            prepend-inner-icon="mdi-form-textbox" />
        </v-col>
        <v-col>
          <vf-autocomplete
            v-model="selectedProducts"
            :items="productOptions"
            :loading="loadingProducts"
            :error-messages="productErrors"
            :label="$tc('product', 2)"/>
        </v-col>
      </v-row>
      <v-row>
        <v-col>
          <vf-autocomplete
            v-model="selectedViolations"
            :items="violationOptions"
            :error-messages="violationErrors"
            :label="$tc('violation', 2)"/>
        </v-col>
      </v-row>
      <v-row>
        <v-col>
          <div class="d-flex align-center">
            <date-picker
              v-model="startDate"
              :timezone="timezone"
              :output-moment="true"
              :label="$t('start_date')"
              prepend-inner-icon="mdi-calendar"
              :error-messages="dateRangeErrors"
              :reset-on-timezone-change="context === 'create'"/>
            <span class="text-subtitle-1 mx-2">{{ $t('to') }}</span>
            <date-picker
              v-model="endDate"
              :timezone="timezone"
              :output-moment="true"
              :end-of-day="true"
              :label="$t('end_date')"
              prepend-inner-icon="mdi-calendar"
              :error="dateRangeErrors.length > 0"
              :reset-on-timezone-change="context === 'create'"/>
          </div>
        </v-col>
      </v-row>
      <date-selector-shortcuts :lowerBound.sync="startDate" :upperBound.sync="endDate" :timezone="timezone"/>
    </v-form>
  </div>
</template>

<script>
import VfAutocomplete from '@/components/shared/VfAutocomplete'
import DatePicker from '@/components/shared/DatePicker'
import DateSelectorShortcuts from '@/components/report/DateSelectorShortcuts'
import CurrentUserMixin from '@/components/mixins/CurrentUserMixin'
import ApiUtilitiesMixin from '@/components/mixins/ApiUtilitiesMixin'
import ValidationMixin from '@/components/mixins/ValidationMixin'
import ReportFormMixin from '@/components/report/ReportFormMixin'
import RouteViolationRecord from '@/orm/models/RouteViolationRecord'
import ReportUtils from '@/components/report/ReportUtils'
import { required, requiredIf } from 'vuelidate/lib/validators'
import { get, map } from 'lodash'

export default {
  name: 'MissedStopsReportForm',
  components: {
    VfAutocomplete,
    DatePicker,
    DateSelectorShortcuts
  },
  mixins: [
    CurrentUserMixin,
    ApiUtilitiesMixin,
    ValidationMixin,
    ReportFormMixin
  ],
  validations: {
    localReport: {
      name: {
        required: requiredIf(function () {
          return this.action !== 'create'
        })
      }
    },
    selectedProducts: {
      required
    },
    selectedViolations: {
      required
    },
    dateRange: {
      rangeIsValid: function () {
        return ReportUtils.dateRangeIsValid(this.startDate, this.endDate)
      }
    }
  },
  data () {
    return {
      reportType: 'SiteViolationReport',
      startDate: null,
      endDate: null,
      loadingProducts: false,
      loadedProducts: false,
      availableProducts: [],
      selectedProducts: [],
      selectedViolations: []
    }
  },
  computed: {
    /**
     * Options property for violations picker
     * @returns Object
     */
    violationOptions () {
      return [
        { text: this.$t('fatal_violation:cold'), value: RouteViolationRecord.TYPE_COLD },
        { text: this.$t('fatal_violation:hot'), value: RouteViolationRecord.TYPE_HOT },
        { text: this.$t('fatal_violation:no-data'), value: RouteViolationRecord.TYPE_NODATA },
        { text: this.$t('fatal_violation:time-gap'), value: RouteViolationRecord.TYPE_GAP }
      ]
    },
    /**
     * Options property for products picker
     */
    productOptions () {
      return map(this.availableProducts, (product) => {
        return {
          text: product.name,
          value: product.id
        }
      })
    },
    nameErrors () {
      return this.validationErrors([{
        prop: this.$v.localReport.name,
        message: this.$t('required')
      }])
    },
    violationErrors () {
      return this.validationErrors([{
        prop: this.$v.selectedViolations,
        message: this.$t('reports_ui.site_violation_report.select_a_violation_type')
      }])
    },
    productErrors () {
      return this.validationErrors([{
        prop: this.$v.selectedProducts,
        message: this.$t('reports_ui.site_violation_report.select_a_product')
      }])
    },
    dateRangeErrors () {
      return this.validationErrors([{
        prop: this.$v.dateRange,
        message: this.$t('invalid_date_range')
      }])
    }
  },
  methods: {
    defaultReportName () {
      return this.$t('reports_ui.site_violation_report.name')
    },
    /** Hook to populate report (called from mixin) */
    populateReportForm () {
      this.fetchProducts()
      this.preSelectViolations()
      this.preSelectDates()
    },
    /** Build report params (called from mixin) */
    buildReportParameters () {
      const reportParameters = {}
      reportParameters.violationTypes = this.selectedViolations
      reportParameters.productIds = this.selectedProducts
      reportParameters.startDate = this.startDate.format()
      reportParameters.endDate = this.endDate.format()
      reportParameters.timezone = this.timezone
      reportParameters.customerId = get(this, 'localReport.parameters.customerId') || this.customerContext.id
      return reportParameters
    },
    /**
     * Using a previous report, find all selected violations and set the state of this component to match, default to
     * selecting all violations.
     *
     * @return undefined
     */
    preSelectViolations () {
      const preSelectedViolations = get(this, 'report.parameters.violationTypes')
      if (preSelectedViolations) {
        this.selectedViolations = preSelectedViolations
      } else {
        this.selectedViolations = map(this.violationOptions, x => x.value)
      }
    },
    /**
     * Using a previous report, find all selected products and set the state of this component to match, default to
     * selecting all products.
     *
     * @return undefined
     */
    preSelectProducts () {
      const preSelectedProductIds = get(this, 'report.parameters.productIds')
      if (preSelectedProductIds) {
        this.selectedProducts = preSelectedProductIds
      } else {
        this.selectedProducts = map(this.productOptions, x => x.value)
      }
    },

    /**
     * Fetch all applicable products from the back-end and update this form with those results. When results are
     * received update the form state to pre-select all products or all products on a previous report if using a
     * previous report as a template.
     *
     * @return undefined
     */
    async fetchProducts () {
      try {
        this.loadingProducts = true
        const result = await this.$orm('Product').findAll({
          customer_id: this.customerContext.Id
        })
        this.availableProducts = result.getItems()
        this.preSelectProducts()
      } catch (error) {
        this.handleError(error)
      } finally {
        this.loadingProducts = false
        this.loadedProducts = true
      }
    }
  }
}
</script>

<style scoped>

</style>
